import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, setSessionToken } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STRIPE_LINK_AVANZADO = process.env.REACT_APP_STRIPE_LINK_AVANZADO || 'https://buy.stripe.com/dRm5kEgnX7aBaUfdeIbMQ01';
const STRIPE_LINK_PROFESIONAL = process.env.REACT_APP_STRIPE_LINK_PROFESIONAL || 'https://buy.stripe.com/00w6oI8Vv7aB0fBgqUbMQ00';

export const SelectPlan = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  const handlePromoCode = async (e) => {
    e.preventDefault();
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const response = await axios.post(`${API}/profile/promo-code`, { code: promoCode.trim() });
      toast.success(response.data.message);
      // Refrescar usuario
      const me = await axios.get(`${API}/auth/me`);
      setUser(me.data);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050508] p-6 md:p-12 flex items-center justify-center">
      <div className="max-w-7xl mx-auto w-full">
        <div className="text-center mb-12 animate-[fadeIn_0.5s_ease-out]">
          <h1
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            Elige tu plan
          </h1>
          <p className="text-xl md:text-2xl text-white/60">
            Comienza tu viaje inmobiliario con E-Home
          </p>

          {/* Código promocional */}
          <form onSubmit={handlePromoCode} className="mt-6 flex items-center justify-center gap-3">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              placeholder="¿Tienes un código promocional?"
              className="px-5 py-3 rounded-xl text-white font-medium w-72 bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-yellow-500/40"
            />
            <button
              type="submit"
              disabled={promoLoading}
              className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold px-6 py-3 rounded-xl hover:opacity-90 transition-all duration-200 disabled:opacity-60"
            >
              {promoLoading ? '...' : 'Aplicar'}
            </button>
          </form>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {/* Plan Usuario - GRATIS */}
          <div
            className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:border-white/20 transition-all duration-300"
            data-testid="plan-usuario"
          >
            <div className="text-center mb-6">
              <div className="inline-block bg-white/10 text-white/60 px-6 py-2 rounded-full font-bold uppercase tracking-wide text-sm mb-4">
                GRATIS
              </div>
              <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
                Usuario
              </h2>
              <div className="text-5xl font-bold text-white mb-2">€0<span className="text-xl text-white/40">/mes</span></div>
              <p className="text-sm text-white/40">Siempre gratis. Para empezar.</p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="text-yellow-500 text-xl mr-2">✓</span>
                <span className="text-base text-white/70">Mapa de búsqueda</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 text-xl mr-2">✓</span>
                <span className="text-base text-white/70">Mi Perfil</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 text-xl mr-2">✓</span>
                <span className="text-base text-white/70">Mensajería básica</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 text-xl mr-2">✓</span>
                <span className="text-base text-white/70">Mis Propiedades (máx 2)</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 text-xl mr-2">✓</span>
                <span className="text-base text-white/70">Publicar (máx 2)</span>
              </li>
              <li className="flex items-start">
                <span className="text-white/30 text-xl mr-2">✗</span>
                <span className="text-base text-white/30">Datos IA difuminados</span>
              </li>
            </ul>

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-white/5 border border-white/10 text-white/60 font-bold uppercase tracking-widest py-4 rounded-2xl hover:bg-white/10 transition-all duration-300"
            >
              Plan Actual
            </button>
          </div>

          {/* Plan Usuario Avanzado - €4.99 */}
          <div
            className="bg-white/5 border border-yellow-500/30 p-8 rounded-3xl hover:border-yellow-500/60 transition-all duration-300 relative"
            data-testid="plan-avanzado"
          >
            <div className="absolute -top-3 -right-3 bg-yellow-500 text-black px-4 py-1 rounded-full font-bold uppercase text-xs rotate-12 shadow-xl">
              Popular
            </div>

            <div className="text-center mb-6">
              <div className="inline-block bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 px-6 py-2 rounded-full font-bold uppercase tracking-wide text-sm mb-4">
                Avanzado
              </div>
              <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
                Usuario Avanzado
              </h2>
              <div className="text-5xl font-bold text-white mb-2">€4.99<span className="text-xl text-white/40">/mes</span></div>
              <p className="text-sm text-white/40">Lo esencial para invertir mejor</p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="text-yellow-500 text-xl mr-2">★</span>
                <span className="text-base text-white font-semibold">Todo lo de Usuario +</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 text-xl mr-2">★</span>
                <span className="text-base text-white/70">Propiedades ilimitadas</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 text-xl mr-2">★</span>
                <span className="text-base text-white/70">Datos IA claros (ROI, impuestos)</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 text-xl mr-2">★</span>
                <span className="text-base text-white/70">Knowledge (blog formativo)</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 text-xl mr-2">★</span>
                <span className="text-base text-white/70">Invest (Asistente IA 24/7)</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 text-xl mr-2">★</span>
                <span className="text-base text-white/70">Comunidad</span>
              </li>
            </ul>

            <a
              href={STRIPE_LINK_AVANZADO}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold uppercase tracking-widest py-4 rounded-2xl hover:opacity-90 transition-all duration-300 text-center"
            >
              Suscribirme
            </a>
          </div>

          {/* Plan Profesional - €24.99 */}
          <div
            className="bg-gradient-to-br from-yellow-500/20 to-amber-500/10 border border-yellow-500/50 p-8 rounded-3xl hover:border-yellow-400 transition-all duration-300 relative"
            data-testid="plan-profesional"
          >
            <div className="absolute -top-3 -right-3 bg-yellow-500 text-black px-4 py-1 rounded-full font-bold uppercase text-xs rotate-12 shadow-xl">
              Pro
            </div>

            <div className="text-center mb-6">
              <div className="inline-block bg-yellow-500 text-black px-6 py-2 rounded-full font-bold uppercase tracking-wide text-sm mb-4">
                Profesional
              </div>
              <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
                Profesional
              </h2>
              <div className="text-5xl font-bold text-white mb-2">€24.99<span className="text-xl text-white/40">/mes</span></div>
              <p className="text-sm text-white/40">Todo el poder de E-Home</p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="text-yellow-400 text-xl mr-2">★</span>
                <span className="text-base text-white font-semibold">Todo lo de Avanzado +</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 text-xl mr-2">★</span>
                <span className="text-base text-white/70">CRM/Organizaciones</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 text-xl mr-2">★</span>
                <span className="text-base text-white/70">Mis Tareas</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 text-xl mr-2">★</span>
                <span className="text-base text-white/70">Análisis avanzados (Plusvalía)</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 text-xl mr-2">★</span>
                <span className="text-base text-white/70">Soporte prioritario</span>
              </li>
            </ul>

            <a
              href={STRIPE_LINK_PROFESIONAL}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold uppercase tracking-widest py-4 rounded-2xl hover:opacity-90 transition-all duration-300 text-center"
            >
              Suscribirme
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
